import { Fragment, ReactElement, useCallback, useMemo, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Button from '../../components/Button';
import { FitBounds, BoundsListener } from '../../components/MapBoundsComponents';
import {
  useGatesWMinRoute, useGateDuplicateAnalysis,
  useMergeGates, useCleanupOrphanGates, useMergeIdenticalGates,
  useMergeRoutes,
} from '../../hooks/useGates';
import {
  useRoutesForManagement, useUpdateRoute, useRetrieveAddresses,
} from '../../hooks/useRoutes';
import { useUpdateGate } from '../../hooks/useGates';
import { useGateSummariesWithCluster, useGateFocus, useMapBounds } from '../../hooks/useGateMap';
import { createGateIcon } from '../../utils/gateMapUtils';
import { deleteGates } from '../../api/gates';
import { deleteRoute } from '../../api/routes';
import markerIconPng from '../../icons/marker-icon-green.png';
import endMarkerIconPng from '../../icons/marker-icon-red.png';
import { GateSummaryWithCluster } from '../../types/GateSummary';
import { DuplicateGroup, Gate } from '../../types/Gate';
import { RouteWithGates } from '../../api/routes';
import { HiOutlineChevronDown, HiOutlineChevronRight } from 'react-icons/hi2';
import { clusterRoutes } from '../../utils/routeClustering';

/* ─── Main Component ─── */

export default function RouteManagement(): ReactElement {
  const {
    data: routes,
    isPending,
    isError,
    refetch,
  } = useRoutesForManagement();

  // Gate data still needed for map and duplicate analysis
  const { data: gatesRaw } = useGatesWMinRoute();
  const gateSummaries = useGateSummariesWithCluster(gatesRaw);

  const [expandedClusters, setExpandedClusters] = useState<Set<number>>(
    new Set(),
  );
  const [expandedRoutes, setExpandedRoutes] = useState<Set<string>>(
    new Set(),
  );
  const [filterMode, setFilterMode] = useState<
    'all' | 'merged' | 'unused' | 'duplicates'
  >('all');
  const [editMode, setEditMode] = useState(false);
  const [selectedRouteIds, setSelectedRouteIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectedGateIds, setSelectedGateIds] = useState<Set<string>>(
    new Set(),
  );
  const [expandedDupGroups, setExpandedDupGroups] = useState<Set<number>>(
    new Set(),
  );

  const {
    data: dupAnalysis,
    isPending: isDupPending,
    isError: isDupError,
  } = useGateDuplicateAnalysis(filterMode === 'duplicates');

  const mergeMutation = useMergeGates();
  const cleanupMutation = useCleanupOrphanGates();
  const mergeIdenticalMutation = useMergeIdenticalGates();
  const mergeRoutesMutation = useMergeRoutes();
  const updateRouteMutation = useUpdateRoute();
  const updateGateMutation = useUpdateGate();
  const retrieveAddressesMutation = useRetrieveAddresses();

  /* ── Duplicate-panel merge (unchanged) ── */
  const handleMergeGroup = useCallback(
    (gateIds: string[]) => {
      const confirmed = window.confirm(
        `Merge ${gateIds.length} gates into 1? ` +
          'Sessions will be re-pointed to the gate with the most sessions.',
      );
      if (!confirmed) return;
      mergeMutation.mutate(gateIds, {
        onSuccess: () => {
          setExpandedDupGroups(new Set());
          refetch();
        },
        onError: (err) => {
          console.error('Failed to merge gates:', err);
          alert('Failed to merge gates. Please try again.');
        },
      });
    },
    [mergeMutation, refetch],
  );

  /* ── Filter routes ── */
  const filteredRoutes = useMemo(() => {
    if (!routes) return [];
    switch (filterMode) {
      case 'unused':
        return routes.filter((r) => r.total_session_count === 0);
      case 'merged':
        return routes.filter(
          (r) =>
            r.gates &&
            r.gates.some((g) => g.creation_mode === 3),
        );
      case 'all':
      default:
        return routes;
    }
  }, [routes, filterMode]);

  const clusteredRoutes = useMemo(
    () => clusterRoutes(filteredRoutes),
    [filteredRoutes],
  );

  /* ── Edit-mode handlers ── */

  const handleMergeIdentical = useCallback(
    (routeId: string) => {
      const ok = window.confirm(
        'Merge gates with identical coordinates on this route?',
      );
      if (!ok) return;
      mergeIdenticalMutation.mutate(routeId, {
        onSuccess: (res) => {
          refetch();
          if (res.groups_merged === 0) {
            alert('No identical gates found on this route.');
          }
        },
        onError: (err) => {
          console.error('Failed to merge identical gates:', err);
          alert('Failed to merge identical gates.');
        },
      });
    },
    [mergeIdenticalMutation, refetch],
  );

  const handleSelectUnused = useCallback(() => {
    if (!routes) return;
    const unused = new Set<string>();
    for (const r of routes) {
      if (r.total_session_count === 0) unused.add(r.id);
    }
    setSelectedRouteIds(unused);
  }, [routes]);

  const handleDeleteSelectedRoutes = useCallback(async () => {
    if (!routes) return;
    const gateIds: string[] = [];
    const deletableRouteIds: string[] = [];
    let skippedWithSessions = 0;
    for (const r of routes) {
      if (!selectedRouteIds.has(r.id)) continue;
      if (r.total_session_count > 0) {
        skippedWithSessions++;
        continue;
      }
      deletableRouteIds.push(r.id);
      if (r.gates) {
        for (const g of r.gates) gateIds.push(g.id);
      }
    }
    if (deletableRouteIds.length === 0) {
      if (skippedWithSessions > 0) {
        alert(
          `${skippedWithSessions} route(s) have sessions ` +
            'and cannot be deleted.',
        );
      }
      return;
    }
    let msg = `Delete ${deletableRouteIds.length} orphan route(s)`;
    if (gateIds.length) msg += ` and ${gateIds.length} gate(s)`;
    msg += '?';
    if (skippedWithSessions > 0) {
      msg += ` (${skippedWithSessions} route(s) with sessions skipped)`;
    }
    if (!window.confirm(msg)) return;
    try {
      if (gateIds.length > 0) {
        await deleteGates(gateIds);
      }
      await Promise.all(
        deletableRouteIds.map((id) => deleteRoute(id)),
      );
      setSelectedRouteIds(new Set());
      refetch();
    } catch (error) {
      console.error('Failed to delete:', error);
      alert('Failed to delete. Please try again.');
    }
  }, [routes, selectedRouteIds, refetch]);

  const handleMergeRoutes = useCallback(() => {
    const ids = Array.from(selectedRouteIds);
    if (ids.length < 2) return;
    const ok = window.confirm(
      `Merge ${ids.length} routes into one averaged route? ` +
        'Gates will be re-assigned where they intersect.',
    );
    if (!ok) return;
    mergeRoutesMutation.mutate(ids, {
      onSuccess: () => {
        setSelectedRouteIds(new Set());
        refetch();
      },
      onError: (err: any) => {
        console.error('Failed to merge routes:', err);
        const msg =
          err?.response?.data?.message ||
          'Failed to merge routes.';
        alert(msg);
      },
    });
  }, [selectedRouteIds, mergeRoutesMutation, refetch]);

  const handleRetrieveAddresses = useCallback(() => {
    const ids = Array.from(selectedRouteIds);
    if (!ids.length) return;
    retrieveAddressesMutation.mutate(ids, {
      onSuccess: (res) => {
        refetch();
        alert(`Addresses retrieved for ${res.updated} route(s).`);
      },
      onError: () => alert('Failed to retrieve addresses.'),
    });
  }, [selectedRouteIds, retrieveAddressesMutation, refetch]);

  const handleUpdateRouteDescription = useCallback(
    (routeId: string, description: string) => {
      updateRouteMutation.mutate({ routeId, data: { description } });
    },
    [updateRouteMutation],
  );

  const handleUpdateGateNotes = useCallback(
    (gateId: string, notes: string) => {
      updateGateMutation.mutate({ gateId, data: { notes } });
    },
    [updateGateMutation],
  );

  /* ── Cleanup orphans ── */
  const handleCleanupOrphans = useCallback(() => {
    const count = filteredRoutes.length;
    const ok = window.confirm(
      `Delete all ${count} unused route(s) and their gates? ` +
        'This also removes orphaned routes.',
    );
    if (!ok) return;
    cleanupMutation.mutate(undefined, {
      onSuccess: () => {
        setSelectedGateIds(new Set());
        setSelectedRouteIds(new Set());
        refetch();
      },
      onError: (err) => {
        console.error('Failed to cleanup orphan gates:', err);
        alert('Failed to cleanup orphan gates.');
      },
    });
  }, [cleanupMutation, filteredRoutes.length, refetch]);

  /* ── Selection toggles ── */
  const toggleRouteSelection = useCallback((routeId: string) => {
    setSelectedRouteIds((prev) => {
      const next = new Set(prev);
      if (next.has(routeId)) next.delete(routeId);
      else next.add(routeId);
      return next;
    });
  }, []);

  const toggleCluster = useCallback((clusterId: number) => {
    setExpandedClusters((prev) => {
      const next = new Set(prev);
      if (next.has(clusterId)) next.delete(clusterId);
      else next.add(clusterId);
      return next;
    });
  }, []);

  const toggleRoute = useCallback((routeId: string) => {
    setExpandedRoutes((prev) => {
      const next = new Set(prev);
      if (next.has(routeId)) next.delete(routeId);
      else next.add(routeId);
      return next;
    });
  }, []);

  /* ── Map ── */
  const {
    activeGateId,
    selectedGate,
    fitBoundsTrigger,
    handleFocusGate,
    handleZoomAll,
  } = useGateFocus(gateSummaries);

  const { bounds, mapCenter, showBothGates, handleMapBoundsChange } =
    useMapBounds(
      gateSummaries,
      selectedGate,
      selectedGate ? 'selected' : 'all',
    );

  const getGateIcon = useCallback(
    (type: 'start' | 'end', label: string) =>
      createGateIcon(type, label, markerIconPng, endMarkerIconPng),
    [],
  );

  /* ── Render ── */
  return (
    <div className="cr-w-full">
      <SectionHeader title="Route Management" isLoading={isPending} />

      <div className="cr-mt-6 cr-grid cr-gap-6">
        {/* ── Map ── */}
        <div
          className={
            'cr-relative cr-h-[500px] cr-overflow-hidden ' +
            'cr-rounded-xl cr-bg-white cr-shadow-sm'
          }
        >
          {isPending && (
            <div
              className={
                'cr-flex cr-h-full cr-items-center cr-justify-center'
              }
            >
              <Spinner isFetching={true} />
            </div>
          )}
          {isError && !isPending && (
            <div
              className={
                'cr-flex cr-h-full cr-items-center ' +
                'cr-justify-center cr-text-red-600'
              }
            >
              Unable to load routes. Please try again later.
            </div>
          )}
          {!isError && !isPending && gateSummaries.length === 0 && (
            <div
              className={
                'cr-flex cr-h-full cr-items-center cr-justify-center'
              }
            >
              No gates available for this team yet.
            </div>
          )}
          {!isError && !isPending && gateSummaries.length > 0 && (
            <>
              {selectedGate && gateSummaries.length > 1 && (
                <div className="cr-absolute cr-right-4 cr-top-4 cr-z-[1000]">
                  <button
                    type="button"
                    onClick={handleZoomAll}
                    className={
                      'cr-rounded-md cr-bg-white cr-px-3 cr-py-1 ' +
                      'cr-text-xs cr-font-semibold cr-text-[#03365D] ' +
                      'cr-shadow-sm hover:cr-bg-cirrusBlue-25'
                    }
                  >
                    All Gates
                  </button>
                </div>
              )}
              <MapContainer
                center={mapCenter}
                zoom={13}
                scrollWheelZoom
                className="cr-h-full cr-w-full"
              >
                <FitBounds bounds={bounds} trigger={fitBoundsTrigger} />
                <BoundsListener onBoundsChange={handleMapBoundsChange} />
                <TileLayer
                  attribution={
                    '&copy; <a href="https://www.openstreetmap.org/' +
                    'copyright">OpenStreetMap</a> contributors'
                  }
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {gateSummaries.map((marker) => (
                  <Fragment key={marker.id}>
                    <Marker
                      position={marker.start}
                      icon={getGateIcon('start', String(marker.order))}
                    >
                      <GatePopup marker={marker} label="Start gate" />
                    </Marker>
                    {showBothGates && marker.end && (
                      <Marker
                        position={marker.end}
                        icon={getGateIcon('end', String(marker.order))}
                      >
                        <GatePopup marker={marker} label="End gate" />
                      </Marker>
                    )}
                  </Fragment>
                ))}
              </MapContainer>
            </>
          )}
        </div>

        {/* ── Filter bar + Edit ── */}
        <div className="cr-rounded-xl cr-bg-white cr-p-6 cr-shadow-sm">
          <div
            className={
              'cr-mb-4 cr-flex cr-items-center cr-justify-between'
            }
          >
            <div className="cr-flex cr-items-center cr-gap-6">
              <FilterRadio
                value="all"
                label="All"
                current={filterMode}
                onChange={() => {
                  setFilterMode('all');
                  setSelectedRouteIds(new Set());
                  setSelectedGateIds(new Set());
                }}
              />
              <FilterRadio
                value="merged"
                label="Merged"
                current={filterMode}
                onChange={() => {
                  setFilterMode('merged');
                  setSelectedRouteIds(new Set());
                  setSelectedGateIds(new Set());
                }}
              />
              <FilterRadio
                value="unused"
                label="Unused"
                current={filterMode}
                onChange={() => {
                  setFilterMode('unused');
                  setSelectedRouteIds(new Set());
                  setSelectedGateIds(new Set());
                }}
              />
              <FilterRadio
                value="duplicates"
                label="Duplicates"
                current={filterMode}
                onChange={() => {
                  setFilterMode('duplicates');
                  setSelectedRouteIds(new Set());
                  setSelectedGateIds(new Set());
                  setExpandedDupGroups(new Set());
                }}
              />
              <Button
                onClick={() => setEditMode((p) => !p)}
                className={
                  editMode
                    ? 'cr-bg-cirrusBlue-100 cr-text-white'
                    : ''
                }
              >
                Edit
              </Button>
            </div>

            {/* ── Edit-mode action buttons ── */}
            <div className="cr-flex cr-gap-2">
              {editMode && selectedRouteIds.size >= 2 && (
                <Button
                  onClick={handleMergeRoutes}
                  disabled={mergeRoutesMutation.isPending}
                >
                  {mergeRoutesMutation.isPending
                    ? 'Merging...'
                    : `Merge Selected (${selectedRouteIds.size})`}
                </Button>
              )}
              {editMode && selectedRouteIds.size > 0 && (
                <Button
                  onClick={handleRetrieveAddresses}
                  disabled={retrieveAddressesMutation.isPending}
                >
                  {retrieveAddressesMutation.isPending
                    ? 'Retrieving...'
                    : `Retrieve Addresses (${selectedRouteIds.size})`}
                </Button>
              )}
              {editMode && (
                <Button onClick={handleSelectUnused}>
                  Select Unused
                </Button>
              )}
              {editMode && selectedRouteIds.size > 0 && (
                <Button
                  onClick={handleDeleteSelectedRoutes}
                  className={
                    'cr-bg-red-600 cr-text-white hover:cr-bg-red-700'
                  }
                >
                  Delete Selected ({selectedRouteIds.size})
                </Button>
              )}
              {!editMode &&
                filterMode === 'unused' &&
                filteredRoutes.length > 0 && (
                  <Button
                    onClick={handleCleanupOrphans}
                    className={
                      'cr-bg-red-600 cr-text-white ' +
                      'hover:cr-bg-red-700'
                    }
                    disabled={cleanupMutation.isPending}
                  >
                    {cleanupMutation.isPending
                      ? 'Deleting...'
                      : `Delete All Unused (${filteredRoutes.length})`}
                  </Button>
                )}
            </div>
          </div>

          {/* ── Duplicate analysis panel ── */}
          {filterMode === 'duplicates' && (
            <DuplicateAnalysisPanel
              dupAnalysis={dupAnalysis ?? null}
              isPending={isDupPending}
              isError={isDupError}
              expandedGroups={expandedDupGroups}
              onToggleGroup={(idx) => {
                setExpandedDupGroups((prev) => {
                  const next = new Set(prev);
                  if (next.has(idx)) next.delete(idx);
                  else next.add(idx);
                  return next;
                });
              }}
              onMergeGroup={handleMergeGroup}
              isMerging={mergeMutation.isPending}
            />
          )}

          {/* ── Route-centric list ── */}
          {filterMode !== 'duplicates' && (
            <div className="cr-flex cr-flex-col cr-gap-2">
              {clusteredRoutes.map((cluster) => {
                const isClusterExpanded = expandedClusters.has(
                  cluster.clusterId,
                );
                return (
                  <div
                    key={cluster.clusterId}
                    className={
                      'cr-overflow-hidden cr-rounded-lg ' +
                      'cr-border cr-border-gray-200'
                    }
                  >
                    {/* Cluster header */}
                    <button
                      onClick={() => toggleCluster(cluster.clusterId)}
                      className={
                        'cr-flex cr-w-full cr-items-center ' +
                        'cr-justify-between cr-bg-gray-50 cr-p-4 ' +
                        'cr-transition-colors hover:cr-bg-gray-100'
                      }
                    >
                      <div className="cr-flex cr-items-center cr-gap-2">
                        {isClusterExpanded ? (
                          <HiOutlineChevronDown
                            className="cr-h-5 cr-w-5 cr-text-gray-500"
                          />
                        ) : (
                          <HiOutlineChevronRight
                            className="cr-h-5 cr-w-5 cr-text-gray-500"
                          />
                        )}
                        <span className="cr-text-2xl cr-font-semibold">
                          {cluster.clusterName}
                        </span>
                        <span className="cr-text-lg cr-text-gray-500">
                          ({cluster.routes.length} route
                          {cluster.routes.length !== 1 ? 's' : ''})
                        </span>
                      </div>
                    </button>

                    {/* Routes inside cluster */}
                    {isClusterExpanded && (
                      <div className="cr-border-t cr-border-gray-200">
                        {cluster.routes.map((route) => (
                          <RouteRow
                            key={route.id}
                            route={route}
                            isExpanded={expandedRoutes.has(route.id)}
                            isSelected={selectedRouteIds.has(route.id)}
                            editMode={editMode}
                            activeGateId={activeGateId}
                            isMerging={
                              mergeIdenticalMutation.isPending
                            }
                            onToggleExpand={() => toggleRoute(route.id)}
                            onToggleSelect={() =>
                              toggleRouteSelection(route.id)
                            }
                            onFocusGate={handleFocusGate}
                            onMergeIdentical={() =>
                              handleMergeIdentical(route.id)
                            }
                            onUpdateDescription={(desc) =>
                              handleUpdateRouteDescription(
                                route.id, desc,
                              )
                            }
                            onUpdateGateNotes={handleUpdateGateNotes}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Sub-components ─── */

function FilterRadio({
  value,
  label,
  current,
  onChange,
}: {
  value: string;
  label: string;
  current: string;
  onChange: () => void;
}) {
  return (
    <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
      <input
        type="radio"
        name="gateFilter"
        value={value}
        checked={current === value}
        onChange={onChange}
        className={
          'cr-h-4 cr-w-4 cr-border-gray-300 ' +
          'cr-text-cirrusBlue-100 focus:cr-ring-cirrusBlue-100'
        }
      />
      <span className="cr-text-sm cr-font-medium">{label}</span>
    </label>
  );
}

function GatePopup({
  marker,
  label,
}: {
  marker: GateSummaryWithCluster;
  label: string;
}) {
  return (
    <Popup>
      <div className="cr-min-w-[180px]">
        <p className="cr-font-semibold">{marker.popupLabel}</p>
        <p className="cr-text-xs cr-text-gray-500">
          Gate #{marker.order}
        </p>
        <p className="cr-text-xs cr-text-gray-500">{label}</p>
        <p className="cr-text-sm cr-text-gray-600">
          {marker.isValid ? 'Valid' : 'Not yet validated'}
        </p>
        {marker.gate.sessions && marker.gate.sessions.length > 0 && (
          <div className="cr-mt-2 cr-border-t cr-pt-2">
            <p className="cr-text-xs cr-font-semibold">Sessions:</p>
            {marker.gate.sessions.slice(0, 3).map((s, i) => (
              <p key={i} className="cr-text-xs cr-text-gray-600">
                {new Date(s.date).toLocaleDateString()} -{' '}
                {s.description}
              </p>
            ))}
            {marker.gate.sessions.length > 3 && (
              <p className="cr-text-xs cr-italic cr-text-gray-500">
                ...and {marker.gate.sessions.length - 3} more
              </p>
            )}
          </div>
        )}
      </div>
    </Popup>
  );
}

function RouteRow({
  route,
  isExpanded,
  isSelected,
  editMode,
  activeGateId,
  isMerging,
  onToggleExpand,
  onToggleSelect,
  onFocusGate,
  onMergeIdentical,
  onUpdateDescription,
  onUpdateGateNotes,
}: {
  route: RouteWithGates;
  isExpanded: boolean;
  isSelected: boolean;
  editMode: boolean;
  activeGateId: string | null;
  isMerging: boolean;
  onToggleExpand: () => void;
  onToggleSelect: () => void;
  onFocusGate: (gateId: string) => void;
  onMergeIdentical: () => void;
  onUpdateDescription: (desc: string) => void;
  onUpdateGateNotes: (gateId: string, notes: string) => void;
}) {
  const hasMultipleGates = (route.gates?.length ?? 0) > 1;
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState(route.description);

  const saveDescription = () => {
    if (descDraft !== route.description) {
      onUpdateDescription(descDraft);
    }
    setIsEditingDesc(false);
  };

  return (
    <div
      className={
        'cr-border-b cr-border-gray-100 last:cr-border-b-0' +
        (isSelected ? ' cr-bg-cirrusBlue-25' : '')
      }
    >
      {/* Route header */}
      <div
        className={
          'cr-flex cr-items-center cr-justify-between cr-p-4 ' +
          'cr-transition-colors hover:cr-bg-gray-50'
        }
      >
        <div className="cr-flex cr-items-center cr-gap-3">
          {editMode && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              className={
                'cr-h-4 cr-w-4 cr-rounded cr-border-gray-300 ' +
                'cr-text-cirrusBlue-100 ' +
                'focus:cr-ring-cirrusBlue-100'
              }
            />
          )}
          <button
            onClick={onToggleExpand}
            className="cr-flex cr-items-center cr-gap-2"
          >
            {isExpanded ? (
              <HiOutlineChevronDown
                className="cr-h-4 cr-w-4 cr-text-gray-500"
              />
            ) : (
              <HiOutlineChevronRight
                className="cr-h-4 cr-w-4 cr-text-gray-500"
              />
            )}
          </button>
          {isEditingDesc ? (
            <input
              type="text"
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              onBlur={saveDescription}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveDescription();
                if (e.key === 'Escape') {
                  setDescDraft(route.description);
                  setIsEditingDesc(false);
                }
              }}
              autoFocus
              className={
                'cr-rounded cr-border cr-border-gray-300 ' +
                'cr-px-2 cr-py-1 cr-text-sm'
              }
            />
          ) : (
            <span
              className={
                'cr-font-medium' +
                (editMode ? ' cr-cursor-text' : '')
              }
              onClick={(e) => {
                if (editMode) {
                  e.stopPropagation();
                  setDescDraft(route.description);
                  setIsEditingDesc(true);
                }
              }}
            >
              {route.description || route.address}
            </span>
          )}
          <span className="cr-text-sm cr-text-gray-500">
            {route.gate_count} gate
            {route.gate_count !== 1 ? 's' : ''}
          </span>
          <span className="cr-text-sm cr-text-gray-500">
            {route.total_session_count} session
            {route.total_session_count !== 1 ? 's' : ''}
          </span>
          {route.average_count != null &&
            route.average_count > 1 && (
              <span
                className={
                  'cr-rounded-full cr-bg-blue-100 cr-px-2 ' +
                  'cr-py-0.5 cr-text-xs cr-font-medium ' +
                  'cr-text-blue-800'
                }
              >
                avg: {route.average_count}
              </span>
            )}
        </div>
        <div className="cr-flex cr-items-center cr-gap-2">
          {editMode && hasMultipleGates && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onMergeIdentical();
              }}
              disabled={isMerging}
            >
              {isMerging ? 'Merging...' : 'Merge Identical Gates'}
            </Button>
          )}
        </div>
      </div>

      {/* Expanded gate list */}
      {isExpanded && route.gates && route.gates.length > 0 && (
        <div className="cr-border-t cr-border-gray-100 cr-pl-10">
          {route.gates.map((gate) => (
            <GateRow
              key={gate.id}
              gate={gate}
              editMode={editMode}
              isActive={gate.id === activeGateId}
              onFocus={() => onFocusGate(gate.id)}
              onUpdateNotes={(notes) =>
                onUpdateGateNotes(gate.id, notes)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function GateRow({
  gate,
  editMode,
  isActive,
  onFocus,
  onUpdateNotes,
}: {
  gate: Gate;
  editMode: boolean;
  isActive: boolean;
  onFocus: () => void;
  onUpdateNotes: (notes: string) => void;
}) {
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(gate.notes ?? '');

  const bearing =
    gate.route_bearing != null
      ? gate.route_bearing < 0
        ? Math.round(gate.route_bearing + 360)
        : Math.round(gate.route_bearing)
      : null;

  const saveNotes = () => {
    const trimmed = notesDraft.trim();
    if (trimmed !== (gate.notes ?? '')) {
      onUpdateNotes(trimmed || null as any);
    }
    setIsEditingNotes(false);
  };

  return (
    <div
      onClick={onFocus}
      className={
        'cr-flex cr-cursor-pointer cr-items-center ' +
        'cr-justify-between cr-border-b cr-border-gray-50 cr-p-3 ' +
        'cr-transition-colors last:cr-border-b-0 hover:cr-bg-gray-50' +
        (isActive ? ' cr-bg-cirrusBlue-25' : '')
      }
    >
      <div>
        <p className="cr-text-sm cr-font-medium">
          Gate {gate.id.slice(0, 8)}...
        </p>
        <p className="cr-text-xs cr-text-gray-500">
          Distance:{' '}
          {gate.route_distance != null
            ? `${Math.round(gate.route_distance)}m`
            : 'N/A'}
          {' | '}Bearing: {bearing != null ? `${bearing}°` : 'N/A'}
          {' | '}Elevation:{' '}
          {gate.elevation_difference?.toFixed(2) ?? 'N/A'}m
        </p>
        {/* Gate notes — inline editable */}
        {isEditingNotes ? (
          <input
            type="text"
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            onBlur={saveNotes}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveNotes();
              if (e.key === 'Escape') {
                setNotesDraft(gate.notes ?? '');
                setIsEditingNotes(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            autoFocus
            placeholder="Gate notes..."
            className={
              'cr-mt-1 cr-w-full cr-rounded cr-border ' +
              'cr-border-gray-300 cr-px-2 cr-py-1 cr-text-xs'
            }
          />
        ) : (
          <p
            className={
              'cr-text-xs cr-text-gray-500' +
              (editMode ? ' cr-cursor-text hover:cr-underline' : '')
            }
            onClick={(e) => {
              if (editMode) {
                e.stopPropagation();
                setNotesDraft(gate.notes ?? '');
                setIsEditingNotes(true);
              }
            }}
          >
            {gate.notes
              ? `Notes: ${gate.notes}`
              : editMode
                ? 'Click to add notes...'
                : ''}
          </p>
        )}
        {gate.sessions && gate.sessions.length > 0 && (
          <div className="cr-mt-1">
            <p className="cr-text-xs cr-font-semibold cr-text-gray-700">
              {gate.sessions.length} session
              {gate.sessions.length !== 1 ? 's' : ''}:
            </p>
            <div className="cr-pl-2">
              {gate.sessions.slice(0, 3).map((s, i) => (
                <p key={i} className="cr-text-xs cr-text-gray-600">
                  {new Date(s.date).toLocaleDateString()} -{' '}
                  {s.description}
                </p>
              ))}
              {gate.sessions.length > 3 && (
                <p className="cr-text-xs cr-italic cr-text-gray-500">
                  ...and {gate.sessions.length - 3} more
                </p>
              )}
            </div>
          </div>
        )}
        {(!gate.sessions || gate.sessions.length === 0) && (
          <p className="cr-text-xs cr-italic cr-text-gray-400">
            No sessions
          </p>
        )}
      </div>
      <Button
        onClick={(e) => {
          e.stopPropagation();
          onFocus();
        }}
      >
        Center
      </Button>
    </div>
  );
}

/* ─── Duplicate Analysis Sub-component ─── */

function DuplicateAnalysisPanel({
  dupAnalysis,
  isPending,
  isError,
  expandedGroups,
  onToggleGroup,
  onMergeGroup,
  isMerging,
}: {
  dupAnalysis: import('../../types/Gate').DuplicateAnalysis | null;
  isPending: boolean;
  isError: boolean;
  expandedGroups: Set<number>;
  onToggleGroup: (idx: number) => void;
  onMergeGroup: (gateIds: string[]) => void;
  isMerging: boolean;
}) {
  if (isPending) {
    return (
      <div className="cr-flex cr-items-center cr-justify-center cr-py-12">
        <Spinner isFetching={true} />
        <span className="cr-ml-3 cr-text-sm cr-text-gray-500">
          Analysing gates for duplicates...
        </span>
      </div>
    );
  }

  if (isError || !dupAnalysis) {
    return (
      <div className="cr-py-8 cr-text-center cr-text-red-600">
        Failed to load duplicate analysis. Please try again.
      </div>
    );
  }

  const { duplicate_groups: groups, summary } = dupAnalysis;

  if (groups.length === 0) {
    return (
      <div className="cr-py-8 cr-text-center cr-text-gray-500">
        No near-duplicate gates found (threshold: 10m). Total
        gates analysed: {summary.total_gates}.
      </div>
    );
  }

  return (
    <div className="cr-flex cr-flex-col cr-gap-3">
      <div
        className={
          'cr-rounded-lg cr-bg-amber-50 cr-border ' +
          'cr-border-amber-200 cr-p-4'
        }
      >
        <p className="cr-text-sm cr-font-semibold cr-text-amber-800">
          Found {summary.total_duplicate_groups} duplicate group
          {summary.total_duplicate_groups !== 1 ? 's' : ''}{' '}
          affecting {summary.total_sessions_affected} session
          {summary.total_sessions_affected !== 1 ? 's' : ''}{' '}
          across {summary.total_duplicate_gates} gate
          {summary.total_duplicate_gates !== 1 ? 's' : ''}{' '}
          (of {summary.total_gates} total)
        </p>
      </div>

      {groups.map((group: DuplicateGroup, groupIdx: number) => {
        const isExpanded = expandedGroups.has(groupIdx);
        const primaryGate = group.gates[0];

        return (
          <div
            key={groupIdx}
            className={
              'cr-overflow-hidden cr-rounded-lg ' +
              'cr-border cr-border-amber-200'
            }
          >
            <div
              className={
                'cr-flex cr-items-center cr-bg-amber-50 ' +
                'cr-transition-colors hover:cr-bg-amber-100'
              }
            >
              <button
                onClick={() => onToggleGroup(groupIdx)}
                className={
                  'cr-flex cr-flex-1 cr-items-center ' +
                  'cr-justify-between cr-p-4'
                }
              >
                <div className="cr-flex cr-items-center cr-gap-3">
                  {isExpanded ? (
                    <HiOutlineChevronDown
                      className="cr-h-5 cr-w-5 cr-text-amber-600"
                    />
                  ) : (
                    <HiOutlineChevronRight
                      className="cr-h-5 cr-w-5 cr-text-amber-600"
                    />
                  )}
                  <span
                    className={
                      'cr-text-lg cr-font-semibold cr-text-amber-900'
                    }
                  >
                    Group {groupIdx + 1}:{' '}
                    {primaryGate.address || 'Unknown location'}
                  </span>
                  <span
                    className={
                      'cr-rounded-full cr-bg-amber-200 cr-px-2.5 ' +
                      'cr-py-0.5 cr-text-xs cr-font-medium ' +
                      'cr-text-amber-800'
                    }
                  >
                    {group.gate_count} gates
                  </span>
                </div>
                <div
                  className={
                    'cr-text-right cr-text-xs cr-text-amber-700'
                  }
                >
                  <span>Start Δ: {group.max_start_delta_m}m</span>
                  <span className="cr-ml-3">
                    End Δ: {group.max_end_delta_m}m
                  </span>
                </div>
              </button>
              <div className="cr-px-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMergeGroup(group.gates.map((g) => g.id));
                  }}
                  disabled={isMerging}
                  className={
                    'cr-rounded-md cr-bg-amber-600 cr-px-3 ' +
                    'cr-py-1.5 cr-text-xs cr-font-semibold ' +
                    'cr-text-white cr-shadow-sm ' +
                    'hover:cr-bg-amber-700 disabled:cr-opacity-50'
                  }
                >
                  {isMerging ? 'Merging...' : 'Merge'}
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="cr-border-t cr-border-amber-200">
                {group.gates.map((gate, gateIdx) => (
                  <div
                    key={gate.id}
                    className={
                      'cr-border-b cr-border-amber-100 cr-p-4 ' +
                      'last:cr-border-b-0' +
                      (gateIdx === 0 ? ' cr-bg-amber-25' : '')
                    }
                  >
                    <div
                      className={
                        'cr-flex cr-items-start cr-justify-between'
                      }
                    >
                      <div className="cr-pl-7">
                        <p className="cr-font-medium cr-text-gray-900">
                          {gate.route_description ||
                            gate.address ||
                            `Gate ${gate.id.slice(0, 8)}`}
                        </p>
                        <p className="cr-text-xs cr-text-gray-500">
                          Start: [
                          {gate.gates_lat[0]?.toFixed(6)},{' '}
                          {gate.gates_lon[0]?.toFixed(6)}]{' | '}
                          End: [{gate.gates_lat[1]?.toFixed(6)},{' '}
                          {gate.gates_lon[1]?.toFixed(6)}]
                        </p>
                        {gate.route_distance != null && (
                          <p className="cr-text-xs cr-text-gray-500">
                            Distance:{' '}
                            {Math.round(gate.route_distance)}m
                            {gate.route_bearing != null && (
                              <>
                                {' | '}Bearing:{' '}
                                {gate.route_bearing < 0
                                  ? Math.round(
                                      gate.route_bearing + 360,
                                    )
                                  : Math.round(gate.route_bearing)}
                                °
                              </>
                            )}
                          </p>
                        )}
                        <p className="cr-text-xs cr-text-gray-500">
                          Route ID: {gate.route_id.slice(0, 8)}...
                          {' | '}Gate ID: {gate.id.slice(0, 8)}...
                        </p>
                        {gate.sessions.length > 0 ? (
                          <div className="cr-mt-2">
                            <p
                              className={
                                'cr-text-xs cr-font-semibold ' +
                                'cr-text-gray-700'
                              }
                            >
                              {gate.session_count} session
                              {gate.session_count !== 1 ? 's' : ''}:
                            </p>
                            <div className="cr-pl-2">
                              {gate.sessions
                                .slice(0, 5)
                                .map((session, idx) => (
                                  <p
                                    key={idx}
                                    className={
                                      'cr-text-xs cr-text-gray-600'
                                    }
                                  >
                                    {new Date(
                                      session.date,
                                    ).toLocaleDateString()}{' '}
                                    - {session.description}
                                  </p>
                                ))}
                              {gate.sessions.length > 5 && (
                                <p
                                  className={
                                    'cr-text-xs cr-italic ' +
                                    'cr-text-gray-500'
                                  }
                                >
                                  ...and {gate.sessions.length - 5}{' '}
                                  more
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p
                            className={
                              'cr-mt-1 cr-text-xs cr-italic ' +
                              'cr-text-gray-400'
                            }
                          >
                            No sessions
                          </p>
                        )}
                      </div>
                      <div className="cr-flex cr-items-center">
                        <span
                          className={`cr-rounded-full cr-px-2 cr-py-0.5 cr-text-xs cr-font-medium ${
                            gate.session_count > 0
                              ? 'cr-bg-blue-100 cr-text-blue-800'
                              : 'cr-bg-gray-100 cr-text-gray-500'
                          }`}
                        >
                          {gate.session_count} session
                          {gate.session_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
